import {
  cloneElement,
  FC,
  isValidElement,
  ReactElement,
  ReactNode,
  Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Paper, Portal, Tooltip, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { TooltipProps } from '@mui/material/Tooltip';
import { observer } from 'mobx-react-lite';

import { TooltipCardContent } from './TooltipCardContent';
import {
  getTooltipVariantTokens,
  TOOLTIP_SIZE_TOKENS,
  type TooltipSize,
  type TooltipVariant,
} from './tooltipVariants';

export interface TooltipCardFloatingPosition {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
}

export interface TooltipCardProps {
  // ---- Content (Sub 2: configurable content) ----
  /** Optional heading line. */
  title?: ReactNode;
  /** Body content — accepts strings or rich nodes. */
  description?: ReactNode;
  /** Leading icon. */
  icon?: ReactNode;
  /** Footer actions (e.g. buttons). */
  actions?: ReactNode;
  /** Show an X close affordance in the header. */
  showClose?: boolean;

  // ---- Appearance (Sub 2: placement, size, colours) ----
  /** Visual variant. @default 'dark' */
  variant?: TooltipVariant;
  /** Padding / typography preset. @default 'md' */
  size?: TooltipSize;
  /** Popper placement (anchored mode). @default 'top' */
  placement?: TooltipProps['placement'];
  /** Render the little pointer arrow (anchored mode). @default true */
  arrow?: boolean;
  /** Override the variant background colour. */
  backgroundColor?: string;
  /** Override the variant text colour. */
  color?: string;
  /** Max width of the surface. Defaults to the size preset. */
  maxWidth?: number | string;
  /** Stacking order. Defaults to `theme.zIndex.tooltip`. */
  zIndex?: number;
  /** Extra styles merged onto the surface. */
  sx?: SxProps<Theme>;

  // ---- Behaviour (Sub 1) ----
  /** Controlled open state. Omit for an uncontrolled (hover/click) tooltip. */
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  /** Interaction that opens an uncontrolled tooltip. @default 'hover' */
  trigger?: 'hover' | 'click' | 'focus';
  /** Delay (ms) before an uncontrolled tooltip opens on hover. */
  enterDelay?: number;
  /** Delay (ms) before an uncontrolled tooltip closes on leave. */
  leaveDelay?: number;
  /** When true, hovering the tooltip surface itself won't keep it open. */
  disableInteractive?: boolean;
  /** Auto-dismiss after N ms (0 / undefined = never). */
  autoHideDuration?: number;
  /** Dismiss when the surface itself is clicked. */
  closeOnClick?: boolean;
  /** Smoothly scroll the anchor (or card) into view when it opens. */
  scrollIntoView?: boolean;

  // ---- Anchoring ----
  /** Anchor element. When present the tooltip is anchored to it. */
  children?: ReactElement;
  /** Render as a fixed, anchorless floating card instead of an anchored tooltip. */
  floating?: boolean;
  /** Fixed position when `floating`. @default { top: 60, right: 20 } */
  floatingPosition?: TooltipCardFloatingPosition;
}

const mergeRefs =
  <T,>(...refs: (Ref<T> | undefined)[]) =>
  (node: T) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === 'function') ref(node);
      else (ref as { current: T | null }).current = node;
    });
  };

/**
 * Unified, reusable tooltip card.
 *
 * One component for every tooltip in the app: a small hover hint, an attention
 * prompt anchored to a button, or a rich floating nudge card — selected through
 * the `variant`, `size` and `floating` props. It is non-modal (it never blocks
 * the page; other buttons stay clickable) and supports auto-dismiss,
 * dismiss-on-click and scroll-into-view.
 *
 * @see {@link getTooltipVariantTokens} to register a new variant.
 */
export const TooltipCard: FC<TooltipCardProps> = observer(
  ({
    title,
    description,
    icon,
    actions,
    showClose,
    variant = 'dark',
    size = 'md',
    placement = 'top',
    arrow = true,
    backgroundColor,
    color,
    maxWidth,
    zIndex,
    sx,
    open,
    onOpen,
    onClose,
    trigger = 'hover',
    enterDelay,
    leaveDelay,
    disableInteractive,
    autoHideDuration,
    closeOnClick,
    scrollIntoView,
    children,
    floating = false,
    floatingPosition = { top: 60, right: 20 },
  }) => {
    const theme = useTheme();

    const tokens = {
      ...getTooltipVariantTokens(theme)[variant],
      ...(backgroundColor ? { backgroundColor, arrowColor: backgroundColor } : {}),
      ...(color ? { color } : {}),
    };
    const sizeTokens = TOOLTIP_SIZE_TOKENS[size];
    const resolvedMaxWidth = maxWidth ?? sizeTokens.defaultMaxWidth;
    const resolvedZIndex = zIndex ?? theme.zIndex.tooltip;

    const isControlled = open !== undefined;
    const [internalOpen, setInternalOpen] = useState(false);
    const effectiveOpen = isControlled ? Boolean(open) : internalOpen;

    const anchorRef = useRef<HTMLElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);

    const handleClose = useCallback(() => {
      if (!isControlled) setInternalOpen(false);
      onClose?.();
    }, [isControlled, onClose]);

    const handleOpen = useCallback(() => {
      if (!isControlled) setInternalOpen(true);
      onOpen?.();
    }, [isControlled, onOpen]);

    // Auto-dismiss.
    useEffect(() => {
      if (!effectiveOpen || !autoHideDuration) return undefined;
      const timer = setTimeout(handleClose, autoHideDuration);
      return () => clearTimeout(timer);
    }, [effectiveOpen, autoHideDuration, handleClose]);

    // Scroll the anchor / card into view when it opens.
    useEffect(() => {
      if (!effectiveOpen || !scrollIntoView) return;
      const target = floating ? cardRef.current : anchorRef.current;
      target?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [effectiveOpen, scrollIntoView, floating]);

    // Nothing meaningful to show — mirror MUI's "empty title hides the
    // tooltip" behaviour so callers can pass a conditional/empty description.
    const hasContent =
      Boolean(title) || Boolean(description) || Boolean(icon) || Boolean(actions) || Boolean(showClose);

    const contentNode = (
      <TooltipCardContent
        title={title}
        description={description}
        icon={icon}
        actions={actions}
        showClose={showClose}
        onClose={handleClose}
        tokens={tokens}
        size={sizeTokens}
      />
    );

    // ---- Floating (anchorless) card ----
    if (floating) {
      if (!effectiveOpen) return null;
      return (
        <Portal>
          <Paper
            ref={cardRef}
            onClick={closeOnClick ? handleClose : undefined}
            sx={{
              position: 'fixed',
              ...floatingPosition,
              zIndex: resolvedZIndex,
              width: resolvedMaxWidth,
              maxWidth: '90vw',
              p: sizeTokens.padding,
              borderRadius: sizeTokens.borderRadius,
              backgroundColor: tokens.backgroundColor,
              color: tokens.color,
              border: tokens.borderColor ? `1px solid ${tokens.borderColor}` : 'none',
              boxShadow: tokens.boxShadow,
              cursor: closeOnClick ? 'pointer' : 'default',
              animation: 'tooltipCardFloatIn 200ms ease',
              '@keyframes tooltipCardFloatIn': {
                from: { opacity: 0, transform: 'translateY(-6px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
              ...sx,
            }}
          >
            {contentNode}
          </Paper>
        </Portal>
      );
    }

    if (!children || !isValidElement(children)) return null;

    // ---- Anchored tooltip ----
    const isClickTrigger = !isControlled && trigger === 'click';

    const anchor = cloneElement(children as ReactElement<{ ref?: Ref<unknown>; onClick?: (e: unknown) => void }>, {
      ref: mergeRefs(anchorRef, (children as ReactElement & { ref?: Ref<unknown> }).ref),
      ...(isClickTrigger && {
        onClick: (e: unknown) => {
          (children.props as { onClick?: (e: unknown) => void }).onClick?.(e);
          setInternalOpen((prev) => !prev);
        },
      }),
    });

    return (
      <Tooltip
        arrow={arrow}
        placement={placement}
        title={hasContent ? contentNode : ''}
        enterDelay={enterDelay}
        leaveDelay={leaveDelay}
        disableInteractive={disableInteractive}
        {...(isControlled || isClickTrigger
          ? { open: effectiveOpen }
          : { onOpen: handleOpen, onClose: handleClose })}
        {...(isControlled && { onOpen: handleOpen, onClose: handleClose })}
        {...(isClickTrigger && {
          disableHoverListener: true,
          disableFocusListener: true,
          disableTouchListener: true,
        })}
        slotProps={{
          popper: { sx: { zIndex: resolvedZIndex } },
          tooltip: {
            onClick: closeOnClick ? handleClose : undefined,
            sx: {
              backgroundColor: tokens.backgroundColor,
              color: tokens.color,
              maxWidth: resolvedMaxWidth,
              p: sizeTokens.padding,
              borderRadius: sizeTokens.borderRadius,
              border: tokens.borderColor ? `1px solid ${tokens.borderColor}` : 'none',
              boxShadow: tokens.boxShadow,
              cursor: closeOnClick ? 'pointer' : 'default',
              '& .MuiTooltip-arrow': {
                color: tokens.arrowColor,
                ...(tokens.borderColor && {
                  '&::before': { border: `1px solid ${tokens.borderColor}` },
                }),
              },
              ...sx,
            },
          },
        }}
      >
        {anchor}
      </Tooltip>
    );
  },
);
